import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { adminGetAuditLog } from '../../api/admin';
import type { AuditEntry } from '../../api/admin';
import type { Company } from '../../types/auth';
import { useToastStore } from '../../store/useToastStore';
import { TH, fmtDate, usePaginated, PaginationBar } from './shared';

const ACTION_LABELS: Record<string, string> = {
  set_done: 'Erledigt gesetzt',
  unset_done: 'Erledigt entfernt',
  set_note: 'Notiz gesetzt',
  clear_note: 'Notiz gelöscht',
  admin_delete: 'Eintrag gelöscht (Admin)',
  admin_clear_all: 'Alle gelöscht (Admin)',
};

const ACTION_BADGE: Record<string, string> = {
  set_done: 'bg-success-subtle text-success-emphasis',
  unset_done: 'bg-secondary-subtle text-secondary-emphasis',
  set_note: 'bg-info-subtle text-info-emphasis',
  clear_note: 'bg-warning-subtle text-warning-emphasis',
  admin_delete: 'bg-danger-subtle text-danger-emphasis',
  admin_clear_all: 'bg-danger-subtle text-danger-emphasis',
};

interface Props { companies: Company[] }

export default function AuditTab({ companies }: Props) {
  const addToast = useToastStore((s) => s.addToast);
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterCompany, setFilterCompany] = useState('');
  const [searchQ, setSearchQ] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setEntries(await adminGetAuditLog(filterCompany || undefined));
    } catch {
      addToast('Fehler beim Laden des Audit-Logs.', 'danger');
    } finally {
      setLoading(false);
    }
  }, [addToast, filterCompany]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = searchQ.toLowerCase();
    if (!q) return entries;
    return entries.filter((e) =>
      e.displayName.toLowerCase().includes(q) ||
      e.callSessionId.toLowerCase().includes(q) ||
      e.value.toLowerCase().includes(q) ||
      (e.companyName ?? '').toLowerCase().includes(q),
    );
  }, [entries, searchQ]);

  const pag = usePaginated(filtered, 25);

  return (
    <>
      <div className="d-flex align-items-center gap-2 mb-3 flex-wrap">
        <select
          className="form-select form-select-sm"
          style={{ maxWidth: 200 }}
          value={filterCompany}
          onChange={(e) => setFilterCompany(e.target.value)}
        >
          <option value="">Alle Unternehmen</option>
          {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <input
          type="search"
          className="form-control form-control-sm ms-auto"
          style={{ maxWidth: 280 }}
          placeholder="Suche nach Name, Anruf-ID, Notiz…"
          value={searchQ}
          onChange={(e) => setSearchQ(e.target.value)}
        />
        <button className="btn btn-outline-secondary btn-sm" onClick={load} title="Aktualisieren">↻</button>
      </div>

      {loading ? (
        <div className="text-center py-5"><span className="spinner-border" /></div>
      ) : (
        <>
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0" style={{ fontSize: '0.83rem' }}>
              <thead style={TH}>
                <tr>
                  <th className="ps-3">Zeitpunkt</th>
                  <th>Unternehmen</th>
                  <th>Benutzer</th>
                  <th>Aktion</th>
                  <th>Wert</th>
                  <th className="pe-3">Anruf-ID</th>
                </tr>
              </thead>
              <tbody>
                {pag.slice.map((e) => (
                  <tr key={e.id}>
                    <td className="ps-3 text-nowrap">{fmtDate(e.timestamp)}</td>
                    <td>{e.companyName ?? <span className="text-muted">—</span>}</td>
                    <td>{e.displayName}</td>
                    <td><span className={`badge ${ACTION_BADGE[e.action] ?? 'bg-secondary'}`} style={{ fontSize: '0.7rem' }}>{ACTION_LABELS[e.action] ?? e.action}</span></td>
                    <td style={{ maxWidth: 200 }}>
                      {e.value ? (
                        <span className="text-truncate d-block" title={e.value} style={{ maxWidth: 200 }}>{e.value}</span>
                      ) : <span className="text-muted">—</span>}
                    </td>
                    <td className="font-monospace pe-3" style={{ fontSize: '0.72rem' }}>{e.callSessionId}</td>
                  </tr>
                ))}
                {pag.slice.length === 0 && (
                  <tr><td colSpan={6} className="text-center text-muted py-4">Keine Einträge</td></tr>
                )}
              </tbody>
            </table>
          </div>
          <PaginationBar page={pag.page} totalPages={pag.totalPages} pageSize={pag.pageSize} total={pag.total} onPage={pag.setPage} onPageSize={pag.setPageSize} />
        </>
      )}
    </>
  );
}
